import {css, StyleSheet} from 'aphrodite';
import React, {useState} from 'react';

import FolderSelector from 'file-browser/FolderSelector';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH} from 'model-hub/generative-models/GenerativeModelsConstants';
import notifications from 'notifications';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARK, GRAY_DARKER, GRAY_LIGHT, RED} from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import NumericInput from 'ui-library/inputs/NumericInput';
import legacyRunModal from 'ui-library/legacyRunModal';
import Modal from 'ui-library/modals/Modal';
import {RowSelectionMode} from 'ui-library/scrollable/ScrollableTableConstants';
import APIUtil from 'util/APIUtil';
import {NumericInputChangeEvent} from 'util/Types';
import {WorklistType} from 'worklists/WorklistConstants';
import {fetchWorklist} from 'worklists/WorklistDAO';
import WorklistSearchInput from 'worklists/WorklistSearchInput';

import {
  getModelDisplayName,
  getModelIconUrl,
  MAX_BULK_PREDICTION_ITEMS,
  SCIENTIFIC_MODEL,
} from '../ScientificModelConstants';
import BulkPredictionItemsList from './BulkPredictionItemsList';
import type {BulkPredictionItem} from './BulkPredictionUtils';
import {getAvailableTypesForModel, isEntityTypeCompatible, submitBulkPrediction} from './BulkPredictionUtils';
import ComponentSearchModal from './ComponentSearchModal';

interface BulkPredictionModalProps {
  model: string;
  inputTypes: Array<string>;
  onRequestClose: () => void;
  onRequestSubmit: (success: boolean) => void;
}

export default function BulkPredictionModal({
  model,
  inputTypes,
  onRequestClose,
  onRequestSubmit,
}: BulkPredictionModalProps): JSX.Element {
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(
    () => FolderDefaults.get(FolderDefaultsKey.BULK_MODEL_PREDICTION) ?? null
  );
  const [items, setItems] = useState<Array<BulkPredictionItem>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingWorklist, setIsLoadingWorklist] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isBoltzGen = model === SCIENTIFIC_MODEL.BOLTZGEN;
  const [minLength, setMinLength] = useState<number>(20);
  const [maxLength, setMaxLength] = useState<number>(50);
  const [rangeValidationMessage, setRangeValidationMessage] = useState<string | null>(null);

  const availableTypes = getAvailableTypesForModel(inputTypes);
  const isAtCapacity = items.length >= MAX_BULK_PREDICTION_ITEMS;
  const displayName = getModelDisplayName(model);
  const iconUrl = getModelIconUrl(model);

  const getDisabledReason = (): string | null => {
    if (isSubmitting) {
      return 'Submitting...';
    }
    if (selectedFolderId == null) {
      return 'Please select a folder';
    }
    if (items.length === 0) {
      return 'Please add at least one item';
    }
    if (isBoltzGen && rangeValidationMessage != null) {
      return rangeValidationMessage;
    }
    return null;
  };

  const addItems = (newItems: Array<BulkPredictionItem>): void => {
    const existingIds = new Set(items.map((item) => item.id));
    const deduplicated = newItems.filter((item) => !existingIds.has(item.id));
    const skippedCount = newItems.length - deduplicated.length;

    if (items.length + deduplicated.length > MAX_BULK_PREDICTION_ITEMS) {
      notifications.error(
        `Cannot add ${deduplicated.length} items — would exceed the limit of ${MAX_BULK_PREDICTION_ITEMS}. Remove some items first.`
      );
      return;
    }

    if (skippedCount > 0) {
      notifications.info(`${skippedCount} items already in list, skipped`);
    }

    setItems((prev) => [...prev, ...deduplicated]);
  };

  const handleAddFromSearch = async (): Promise<void> => {
    // "Add Items" (not "Add Components") because bulk mode adds separate predictions,
    // not components of a single multimer prediction.
    const selectedEntityIds = (await legacyRunModal(ComponentSearchModal, {
      rowSelectionMode: RowSelectionMode.MULTIPLE,
      availableTypes,
      title: 'Add items',
      submitButtonText: 'Add Items',
    })) as Array<string> | null | undefined;

    if (selectedEntityIds == null || selectedEntityIds.length === 0) {
      return;
    }

    const newItems: Array<BulkPredictionItem> = selectedEntityIds.map((id) => ({
      id,
      entityType: APIUtil.apiIdToType(id),
    }));

    addItems(newItems);
  };

  const handleWorklistSelect = async (worklist: {id: string} | null): Promise<void> => {
    if (worklist == null) {
      return;
    }

    setIsLoadingWorklist(true);
    try {
      const fullWorklist = await fetchWorklist(worklist.id);
      const allItemIds = fullWorklist.itemIds;

      const compatible: Array<BulkPredictionItem> = [];
      let skippedIncompatible = 0;

      for (const itemId of allItemIds) {
        const entityType = APIUtil.apiIdToType(itemId);
        if (isEntityTypeCompatible(entityType, inputTypes)) {
          compatible.push({id: itemId, entityType});
        } else {
          skippedIncompatible++;
        }
      }

      const existingIds = new Set(items.map((item) => item.id));
      const deduplicated = compatible.filter((item) => !existingIds.has(item.id));
      const skippedDuplicates = compatible.length - deduplicated.length;

      if (items.length + deduplicated.length > MAX_BULK_PREDICTION_ITEMS) {
        notifications.error(
          `Cannot add worklist — ${deduplicated.length} items would exceed the limit of ${MAX_BULK_PREDICTION_ITEMS}. Remove some items first.`
        );
        return;
      }

      if (skippedIncompatible > 0 || skippedDuplicates > 0) {
        const parts: Array<string> = [];
        if (skippedIncompatible > 0) {
          parts.push(`${skippedIncompatible} incompatible items skipped`);
        }
        if (skippedDuplicates > 0) {
          parts.push(`${skippedDuplicates} duplicate items skipped`);
        }
        notifications.info(
          `${deduplicated.length} of ${allItemIds.length} items added (${parts.join(', ')})`
        );
      }

      setItems((prev) => [...prev, ...deduplicated]);
    } catch (e) {
      notifications.error('Failed to load worklist items.');
      if (!(e instanceof Error)) {
        throw e;
      }
    } finally {
      setIsLoadingWorklist(false);
    }
  };

  const handleRemoveItem = (id: string): void => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  const validateAndSetRange = (newMin: number | null, newMax: number | null): void => {
    let message: string | null = null;
    if (newMin == null || newMax == null) {
      message = 'Minimum and maximum sequence lengths must be specified.';
    } else if (newMin < 1) {
      message = 'Minimum sequence length must be at least 1.';
    } else if (newMax > MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH) {
      message = `Maximum sequence length cannot exceed ${MAX_BOLTZGEN_DESIGN_PROTEIN_LENGTH}.`;
    } else if (newMin > newMax) {
      message = 'Minimum sequence length cannot be greater than maximum sequence length.';
    }
    setRangeValidationMessage(message);
    if (newMin != null) {
      setMinLength(newMin);
    }
    if (newMax != null) {
      setMaxLength(newMax);
    }
  };

  const disabledReason = getDisabledReason();

  const handleSubmit = async (): Promise<void> => {
    if (disabledReason != null || selectedFolderId == null) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const modelParams = isBoltzGen ? {minLength, maxLength} : undefined;

      await submitBulkPrediction({
        folderId: selectedFolderId,
        model,
        items,
        modelParams,
      });

      FolderDefaults.set(FolderDefaultsKey.BULK_MODEL_PREDICTION, selectedFolderId);
      notifications.success(`${displayName} run created with ${items.length} predictions.`);
      onRequestSubmit(true);
    } catch (e) {
      if (!(e instanceof Error)) {
        throw e;
      }
      setError(e.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div className={css(styles.titleRow)}>
          <img src={iconUrl} alt={displayName} className={css(styles.titleIcon)} />
          <span>Configure {displayName} Run</span>
        </div>
      }
      onRequestClose={onRequestClose}
      closeableOnBackgroundClick={!isSubmitting}
      large={true}
      fullHeight={true}
      bodyPadded={false}
      scrollable={false}
    >
      <div className={css(styles.outerContainer)}>
        <div className={css(styles.content)}>
          {error && (
            <div className={css(styles.errorBanner)}>
              <InlineBanner text={error} bannerType={InlineBannerType.ERROR} />
            </div>
          )}

          {/* Folder Picker */}
          <div className={css(styles.section)}>
            <div className={css(styles.label)}>
              Output Folder <span className={css(styles.required)}>*</span>
            </div>
            <div className={css(styles.folderSelectorContainer)}>
              <FolderSelector
                value={selectedFolderId}
                onChange={(e: {target: {value: string | null}}) => {
                  setSelectedFolderId(e.target.value);
                }}
                allowNone={false}
                enableRedesign={false}
                error=""
                level="append"
                noFolderText="Choose folder"
                readOnly={false}
                requireSubfolderCreatableAuth={false}
                showInventory={false}
                showNotebook={true}
                showRegistries={false}
                unsetOnFetchFail={false}
                checkExistingItemForDataModsMovement={false}
              />
            </div>
          </div>

          {/* Per-Model Parameters (BoltzGen only) */}
          {isBoltzGen && (
            <div className={css(styles.section)}>
              <div className={css(styles.label)}>Generated Sequence Length Range</div>
              <div className={css(styles.paramRow)}>
                <div className={css(styles.paramField)}>
                  <div className={css(styles.paramLabel)}>Minimum</div>
                  <NumericInput
                    className={css(styles.numericInput)}
                    value={minLength}
                    required={true}
                    onChange={(e: NumericInputChangeEvent) => {
                      validateAndSetRange(e.target.numericValue, maxLength);
                    }}
                  />
                </div>
                <div className={css(styles.paramField)}>
                  <div className={css(styles.paramLabel)}>Maximum</div>
                  <NumericInput
                    className={css(styles.numericInput)}
                    value={maxLength}
                    required={true}
                    onChange={(e: NumericInputChangeEvent) => {
                      validateAndSetRange(minLength, e.target.numericValue);
                    }}
                  />
                </div>
              </div>
              {rangeValidationMessage && (
                <div className={css(styles.validationMessage)}>{rangeValidationMessage}</div>
              )}
            </div>
          )}

          {/* Item Addition Controls */}
          <div className={css(styles.section)}>
            <div className={css(styles.label)}>Add Items</div>
            <div className={css(styles.addControlsRow)}>
              <div className={css(styles.addControlGroup)}>
                <div className={css(styles.addControlLabel)}>Search for items</div>
                <div>
                  <Tooltip
                    text={isAtCapacity ? `Item limit of ${MAX_BULK_PREDICTION_ITEMS} reached` : null}
                    isDisabled={!isAtCapacity}
                  >
                    <Button
                      size={ButtonSize.MEDIUM}
                      buttonStyle={ButtonStyle.TERTIARY_BLUE}
                      text="Browse and select"
                      onClick={handleAddFromSearch}
                      disabled={isAtCapacity}
                    />
                  </Tooltip>
                </div>
              </div>
              <div className={css(styles.addControlDivider)}>or</div>
              <div className={css(styles.addControlGroup)}>
                <div className={css(styles.addControlLabel)}>Add from worklist</div>
                <div className={css(styles.worklistSearch)}>
                  <WorklistSearchInput
                    autoFocus={false}
                    disabled={isAtCapacity}
                    onChange={handleWorklistSelect}
                    worklistTypes={[WorklistType.BIOENTITY]}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Predictions Table */}
          <div className={css(styles.section, styles.itemsSection)}>
            <div className={css(styles.label)}>Predictions</div>
            <BulkPredictionItemsList
              items={items}
              onRemoveItem={handleRemoveItem}
              isLoading={isLoadingWorklist}
            />
          </div>
        </div>

        {/* Footer */}
        <div className={css(styles.footer)}>
          <Tooltip text={disabledReason} isDisabled={disabledReason == null}>
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              size={ButtonSize.MEDIUM}
              text={isSubmitting ? 'Creating...' : 'Create Run'}
              disabled={disabledReason != null}
              onClick={handleSubmit}
            />
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    padding: '20px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  titleIcon: {
    width: 24,
    height: 24,
    objectFit: 'contain',
  },
  section: {
    marginBottom: '20px',
  },
  itemsSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  label: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: 600,
    marginBottom: '8px',
  },
  required: {
    color: RED,
  },
  folderSelectorContainer: {
    maxWidth: '350px',
  },
  paramRow: {
    display: 'flex',
    gap: '16px',
  },
  paramField: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  paramLabel: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
  },
  numericInput: {
    width: '100px',
  },
  validationMessage: {
    ...FONT_PROXIMA_13,
    color: RED,
    marginTop: '4px',
  },
  addControlsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  addControlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  addControlLabel: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
  },
  addControlDivider: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARK,
    paddingTop: '22px',
  },
  worklistSearch: {
    width: '250px',
  },
  errorBanner: {
    marginBottom: '16px',
  },
  footer: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
